/* eslint-disable react-hooks/exhaustive-deps */
import { Box, InputAdornment, MenuItem, Pagination } from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';

import { BankAdditionalRoles } from '../../api/enumerations';
import { getAllUsers } from '../../api/users';
import { UserData } from '../../api/users/types';
import Snackbar from '../../components/Snackbar';
import { TitleBox } from '../../components/UI/Box';
import { GridMain } from '../../components/UI/Grid';
import {
  menuItemDefaultStyle,
  StyledLinearProgress,
  StyledTextField,
  TitleTypography,
} from '../../components/UI/Typography';
import { IconPersonMS, IconSearchMS } from '../../constants/icons';
import { selectBankProfileUser } from '../../constants/selectOptions';
import { GlobalContext } from '../../context/global';
import { matchCompanySubdomain } from '../../helpers';
import { useDebounce } from '../../hooks/useDebounce';
import { useStoragedJwt } from '../../hooks/useDecodedJwt';
import useErrorMessage from '../../hooks/useErrorMessage';
import useSnackbar from '../../hooks/useSnackbar';
import { Constants } from './constants';
import NewUserDialog from './NewUserDialog';
import {
  TableContainer,
  SearchBox,
  StyledInputBase,
  PagesContainer,
  LoadingBox,
} from './styles';
import Table from './Table';

export function UsersBankRoles(): JSX.Element {
  const [userRole, setUserRole] = useState(0);
  const [users, setUsers] = useState<UserData[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchRole, setSearchRole] = useState<BankAdditionalRoles>(0);

  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);

  const usersPerPage = 10;

  const { openSnackbar } = useContext(GlobalContext);
  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();
  const decoded = useStoragedJwt();
  const debouncedSearch = useDebounce(searchTerm);

  useEffect(() => {
    if (decoded !== null) {
      const companySubdomain = matchCompanySubdomain();
      const companyData = decoded.user.companies.find(
        (company) => company.subdomain === companySubdomain
      );
      if (companyData) setUserRole(companyData.role);
    }
  }, []);

  const getDataCallback = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getAllUsers(
        page,
        usersPerPage,
        debouncedSearch,
        searchRole
      );

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (!response.data) throw new Error('Algo deu errado, tente novamente.');

      if (response.detail.total_pages)
        setTotalPages(response.detail.total_pages);

      setUsers(response.data);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, searchRole]);

  const updateUsers = (): void => {
    setPage(1);
    getDataCallback();
  };

  useEffect(() => {
    if (debouncedSearch.length > 0) setPage(1);
    else if (page !== 1) setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    getDataCallback();
  }, [getDataCallback]);

  const handleNextPage = (
    event: React.ChangeEvent<unknown>,
    value: number
  ): void => {
    setPage(value);
  };

  return (
    <GridMain>
      <TitleBox>
        <TitleTypography>
          {IconPersonMS}
          {Constants.title}
        </TitleTypography>
        <NewUserDialog
          updateUsers={updateUsers}
          isAdmin={userRole === BankAdditionalRoles.BANK_ADMIN}
        />
      </TitleBox>
      <Box sx={{ display: 'flex' }}>
        <TableContainer>
          <SearchBox>
            <StyledTextField
              id="role"
              select
              label={Constants.role}
              required
              value={searchRole}
              onChange={(e) => setSearchRole(Number(e.target.value))}
              sx={{ width: '260px' }}
            >
              {selectBankProfileUser().map((role) => (
                <MenuItem
                  key={role.value}
                  value={role.value}
                  sx={{ ...menuItemDefaultStyle }}
                >
                  {role.label}
                </MenuItem>
              ))}
            </StyledTextField>
            <StyledInputBase
              placeholder={Constants.search}
              inputProps={{ 'aria-label': 'pesquisar', maxLength: 20 }}
              type="search"
              startAdornment={
                <InputAdornment
                  position="start"
                  sx={(theme) => ({ color: theme.palette.secondary.main })}
                >
                  {IconSearchMS}
                </InputAdornment>
              }
              onChange={(e) => {
                if (e.target.value.length > 0) setSearchTerm(e.target.value);
                if (e.target.value.length === 0) setSearchTerm('');
              }}
              onFocus={(e) => {
                e.target.value = '';
                setSearchTerm('');
              }}
            />
          </SearchBox>
          {loading ? (
            <LoadingBox>
              <StyledLinearProgress />
            </LoadingBox>
          ) : (
            <Box width="100%">
              {users.length > 0 ? (
                <>
                  <Table
                    tableData={users}
                    isAdmin={userRole === BankAdditionalRoles.BANK_ADMIN}
                    updateUsers={updateUsers}
                  />
                  <PagesContainer>
                    <Pagination
                      page={page}
                      count={totalPages}
                      onChange={handleNextPage}
                    />
                  </PagesContainer>
                </>
              ) : (
                <Box>{Constants.noUsersFound}</Box>
              )}
            </Box>
          )}
        </TableContainer>
      </Box>
      {openSnackbar && <Snackbar />}
    </GridMain>
  );
}
